import { DayOfWeek } from '@e2m-customer-portal/ui';

export enum OptimizationGroupId {
  Biogas = '200010',
  InnoTenderGE = '200005',
  InnoTender = '200007',
  PeakShavingBatteries = '200004',
  FleximumBatteries = '200003',
  Danpower = '200001',
  CoLocation = '200014',
  AFRRBatteries = '200017',
}

export type AutomatedParameter<T> = {
  enable_live_parameter: boolean;
  default_value: T | null;
};

export type OptimizationParameterValue =
  | number
  | string
  | boolean
  | AutomatedParameter<any>
  | BillingDataConfig
  | EegSubsidyParametersType
  | PeakWindowType[]
  | ScheduleConstraintType[]
  | TargetPowerScheduleConstraintType[]
  | TargetRatedPowerGroupType[]
  | WeeklyFillLevelConfig
  | null
  | undefined;

export enum AssetType {
  Biogas = 'Biogas',
  FleximumBatteries = 'FleximumBatteries',
  PeakShavingBatteries = 'PeakShavingBatteries',
  InnoTender = 'InnoTender',
  InnoTenderGE = 'InnoTenderGE',
  Danpower = 'Danpower',
  CoLocation = 'CoLocation',
  AFRRBatteries = 'AFRRBatteries',
}

export enum GermanyFederalState {
  '' = '',
  BB = 'Brandenburg',
  BE = 'Berlin',
  BW = 'Baden-Württemberg',
  BY = 'Bayern',
  BYP = 'Bayern Protestant',
  HB = 'Bremen',
  HE = 'Hessen',
  HH = 'Hamburg',
  MV = 'Mecklenburg-Vorpommern',
  NI = 'Niedersachsen',
  NW = 'Nordrhein-Westfalen',
  RP = 'Rheinland-Pfalz',
  SH = 'Schleswig-Holstein',
  SL = 'Saarland',
  SN = 'Sachsen',
  ST = 'Sachsen-Anhalt',
  TH = 'Thüringen',
}

export enum OptimizationHorizonMode {
  next_open_market = 'next_open_market',
  next_monday = 'next_monday',
}

export interface OptimizationGroupParametersType {
  // Meta
  name: string;
  status: string;
  valid_from_timestamp: string;
  asset_type: AssetType;
  horizon_mode?: OptimizationHorizonMode;
  opt_run_schedule?: string | null;
  batteries?: Map<string, BatteryDataType>;
  battery?: BatteryDataType;
  pv?: Map<string, PVSystemDataType> | PVSystemDataType;

  grid_connection_capacity?: number | null;
  grid_connection_capacity_in?: number | null;
  grid_connection_capacity_out?: number | null;
  power_plant_serial_number: number | null; // required
  use_scheduling: boolean | null; // required
  melo_id?: number | null;
  self_consumption?: number | null;
  self_consumption_costs?: number | null;
  afrr_active?: boolean | null;
  afrr_price_factor_pos?: number | null;
  afrr_price_factor_neg?: number | null;
  afrr_pos_implicit_costs?: number | null;
  afrr_neg_implicit_costs?: number | null;
  power_buffer_percentage?: number | null;
  power_buffer_fixed?: number | null;
  power_buffer_coupled_on_pv_on?: boolean | null;
  ppa_price?: number | null;
  market_premium?: number | null;
  robotron_name?: string | null;
  inverter_efficiency?: number | null;
  profit_share?: boolean | null;
  billing_data_config?: BillingDataConfig;
  target_rated_power_groups?: TargetRatedPowerGroupType[];

  // Co-Location
  pv_forecast_buffer?: number | null;

  // Danpower
  device_number?: number | null;
  peak_power?: number | null;
  base_power?: number | null;
  level_time_min?: number | null;
  daily_peak_time_max?: number | null;
  daily_level_changes_max?: number | null;
  eeg_premium?: number | null;
  marginal_price?: number | null;
  eeg_negative_hour_rule_length?: number | null;
  federal_state?: GermanyFederalState | null;
}

export interface BillingDataConfig {
  battery_charge_threshold?: number | null;
  grid_connection_power_threshold?: number | null;
  battery_not_used_volume_threshold?: number | null;
  pv_deviation_threshold_relative?: number | null;
  pv_deviation_threshold_absolute?: number | null;
  grid_feedin_tolerance_absolute?: number | null;
  availability_metric_method?: AvailabilityMetricMethod;
  ppa_billed?: boolean | null;
}

export enum AvailabilityMetricMethod {
  contract = 'contract',
  availability_bit = 'availability_bit',
}

export interface WeeklyFillLevelConfig {
  fill_level: number;
  time: string;
  weekday: DayOfWeek;
}

export interface GasStorageDataType {
  alias?: string | null;
  x_methane?: number | null;
  CAL_METHANE?: number | null;
  lower_soc_limit?: number | null;
  upper_soc_limit?: number | null;
  volume_capacity?: number | null;
  open_afrr_storage_reduction_time_upper?: number | null;
  open_afrr_storage_reduction_time_lower?: number | null;
  closed_afrr_storage_reduction_time_upper?: number | null;
  closed_afrr_storage_reduction_time_lower?: number | null;
  day_ahead_storage_reduction_time_upper?: number | null;
  day_ahead_storage_reduction_time_lower?: number | null;
  breach_reaction_storage_reduction?: number | null;
  breach_reaction_time?: number | null;
  fixed_storage_level_device_number?: number | null;
  soc_initialization_mode?: SoCInitializationMode | null;
  fixed_initial_fill_level?: number | null;
  fixed_weekly_fill_level_config?: WeeklyFillLevelConfig | null;
  fill_level_entry?: AutomatedParameter<{ fill_level: number | null; measured_at: string | null }> | null;
  end_level_tolerance?: number | null;
  short_term_soft_band?: number;
  short_term_soft_band_price?: number | null;
}

export enum SoCInitializationMode {
  automated_measurement = 'automated_measurement',
  manual_measurement = 'manual_measurement',
  fixed_initial_level = 'fixed_initial_level',
  fixed_weekly_fill_level = 'fixed_weekly_fill_level',
}

export interface GasProductionDataType {
  target_rated_power?: number | null; //Will be replaced with target_power_electric
  target_power_electric?: number | null;
  conversion_efficiency?: number | null;
  target_power_schedule_constraints?: TargetPowerScheduleConstraintType[];
}

export interface TargetPowerScheduleConstraintType {
  power: AutomatedParameter<number> & { live_value?: number };
  weekday: DayOfWeek;
}

export enum EEGRegime {
  Pre2016 = 'pre_2016',
  EEG2017 = 'eeg_2017',
  EEG2021 = 'eeg_2021',
  EEG2023v1 = 'eeg_2023_v1',
  EEG2023v2 = 'eeg_2023_v2',
  Exempt = 'exempt_from_negative_price_rule',
}
export interface InitialQhCount {
  count: number;
  timestamp: string;
}

export interface OneThirdQuarterHourRule {
  n_target_quarter_hours: number | null;
  initial_qh_count: InitialQhCount | null;
}

export interface TargetRatedPowerGroupType {
  identifier: string;
  target_rated_power_eeg: number | null;
  name: string | null;
  penalty_lower: number | null;
  penalty_upper: number | null;
  eeg_regime: EEGRegime;
  eeg_premium: number | null;
  schedule_min_mw: number | null;
  schedule_max_mw: number | null;
  eeg_2023_v2_threshold_ct_per_kwh: number | null;
  eighty_five_percent_rule_config?: null;
  one_third_quarter_hour_rule_config?: OneThirdQuarterHourRule | null;
}

export enum Season {
  spring = 'Frühling',
  summer = 'Sommer',
  autumn = 'Herbst',
  winter = 'Winter',
}

export interface PeakWindowType {
  identifier: string;
  season: {
    name: Season;
    end_date: string;
    start_date: string;
  };
  end_soc: number | null; //MWh required
  start_soc: number | null; //MWh required
  end_time_str: string; //HH:MM required
  start_time_str: string; //HH:MM required
  exclude_weekends: boolean;
  additional_exclude_dates: string[] | null;
  federal_state_holidays: string | null;
  exclude_last_days_of_the_year: boolean;
}

export interface PVSystemDataType {
  alias?: string | null;
  device_number: number;
  forecast_id?: number | null;
  installed_power?: number | null;
  accepted_power_deviation?: number | null;
  always_available?: boolean | null;
  eeg_subsidy_parameters?: EegSubsidyParametersType;
}

export interface EegSubsidyParametersType {
  eeg_auction_value?: number | null;
  negative_hours_threshold?: number | null;
}

export interface ScheduleConstraintType {
  identifier: string;
  start_time: string | null;
  end_time: string | null;
  days_of_week: number[] | null;
  minimum_power: number | null;
  maximum_power: number | null;
}

export interface MotorDataType {
  identifier?: string;
  device_number?: number | null;
  alias?: string | null;
  always_available?: boolean | null;
  p_min?: number | null;
  p_max?: number | null;
  efficiency?: number | null;
  schedule_constraints?: ScheduleConstraintType[];
  on_time_min?: number | null;
  off_time_min?: number | null;
  on_time_max?: number | null;
  off_time_max?: number | null;
  daily_starts_min?: number | null;
  daily_starts_max?: number | null;
  weekly_starts_min?: number | null;
  weekly_starts_max?: number | null;
  daily_on_time_max?: number | null;
  daily_on_time_min?: number | null;
  minimal_power_conversion_efficiency?: number | null;
  start_costs?: number | null;
  additional_start_costs?: number | null;
  target_rated_power_group?: string | null;
}

export interface BatteryDataType {
  alias?: string | null; // required
  device_number?: number | null; // required
  power_charge?: number | null; // MW required
  power_discharge?: number | null; // MW required
  energy_capacity?: number | null; // MWh required
  nominal_capacity?: number | null; // MWh required
  efficiency_in?: number | null;
  efficiency_out?: number | null;
  charge_price?: number | null; // EUR
  costs_per_full_cycle?: AutomatedParameter<number>; // EUR
  storage_max?: number | null;
  storage_min?: number | null;
  accepted_capacity_deviation?: number | null;
  cycle_per_year?: number | null;
  cycle_per_day?: number | null;
  prequalified_power?: number | null; // MW required
  fcr_storage_reserve_time?: number | null; // h
  afrr_activation_reserve_time_neg?: number | null; // h
  afrr_activation_reserve_time_pos?: number | null; // h
  peak_windows?: PeakWindowType[]; // required for PeakShavingBatteries
  // CoLocation
  energy_efficiency_in?: number | null;
  energy_efficiency_out?: number | null;
  use_dynamic_power_limits?: boolean | null;
}
